import React, { useMemo, useState, useRef, useEffect } from "react";
import Model, { helper } from "@tripian/model";
import { SvgIcons } from "@tripian/react";
import useCities from "../../../hooks/useCities";
import useTranslate from "../../../hooks/useTranslate";

export type CityOption = {
  id: number;
  label: string;
  payload: {
    destinationId: number;
    destinationName: string;
    coordinate: Model.Coordinate;
    parentName: string;
  };
};

interface CitySearchInputProps {
  selectedCityId?: number;
  onCitySelect: (city: CityOption) => void;
  onClear?: () => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  showBorder?: boolean;
  showValaiStyle?: boolean;
}

const CitySearchInput: React.FC<CitySearchInputProps> = ({
  selectedCityId,
  onCitySelect,
  onClear,
  placeholder,
  className = "",
  inputClassName = "",
  showBorder = false,
  showValaiStyle = false,
}) => {
  const { t } = useTranslate();
  const { cities } = useCities();
  const [searchText, setSearchText] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const destinations = useMemo(() => {
    if (cities) {
      return cities.map(city => ({
        destinationId: city.id,
        destinationName: city.name,
        coordinate: city.coordinate,
        parentName:
          city.parentLocations.length === 0
            ? city.country.name
            : `${city.parentLocations.map(parent => parent.name).join(", ")}, ${city.country.name}`,
      }));
    }
    return [];
  }, [cities]);

  const cityOptions: CityOption[] = useMemo(
    () =>
      destinations
        .sort((a, b) =>
          helper.compareStringForSort(a.destinationName, b.destinationName)
        )
        .map(d => ({
          id: d.destinationId,
          label: `${d.destinationName} - ${d.parentName}`,
          payload: d,
        })),
    [destinations]
  );

  const filteredOptions = useMemo(() => {
    if (!searchText.trim()) return cityOptions.slice(0, 10);
    const searchLower = searchText.toLowerCase();
    return cityOptions
      .filter(option => option.label.toLowerCase().includes(searchLower))
      .slice(0, 10);
  }, [cityOptions, searchText]);

  // Set initial search text from selectedCityId
  useEffect(() => {
    if (selectedCityId && cities) {
      const selectedCity = cities.find(city => city.id === selectedCityId);
      if (selectedCity) {
        setSearchText(selectedCity.name);
      }
    }
  }, [selectedCityId, cities]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCitySelection = (option: CityOption) => {
    setSearchText(option.payload.destinationName);
    setIsDropdownOpen(false);
    onCitySelect(option);
  };

  const handleClear = () => {
    setSearchText("");
    setIsDropdownOpen(false);
    onClear?.();
  };

  const defaultPlaceholder = t("search");

  const getContainerStyle = () => {
    if (showValaiStyle) {
      return "border border-primary-color bg-white";
    }
    return "";
  };

  const getInputStyle = () => {
    if (showBorder) {
      return "rounded-full border-2 border-gray-200 bg-white focus:border-primary-color transition-colors";
    }
    return "";
  };

  return (
    <div className={`relative ${className}`}>
      <div
        className={`relative ${getContainerStyle()}`}
        style={showValaiStyle ? { borderRadius: '30px' } : undefined}
      >
        <SvgIcons.Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${showValaiStyle ? 'text-primary-color' : 'text-gray-400'}`} />
        <input
          ref={inputRef}
          type="text"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setIsDropdownOpen(true);
            if (!e.target.value) {
              onClear?.();
            }
          }}
          onFocus={() => setIsDropdownOpen(true)}
          placeholder={placeholder || defaultPlaceholder}
          className={`w-full pl-12 pr-10 py-4 text-gray-800 placeholder-gray-400 focus:outline-none text-base bg-transparent ${getInputStyle()} ${inputClassName}`}
          style={showValaiStyle ? { borderRadius: '30px' } : undefined}
        />
        {searchText && (
          <button
            onClick={handleClear}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <SvgIcons.SearchClose />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isDropdownOpen && filteredOptions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 max-h-64 overflow-y-auto z-50"
        >
          {filteredOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => handleCitySelection(option)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
            >
              <span className="font-medium text-gray-900">{option.payload.destinationName}</span>
              <span className="text-gray-500 text-sm ml-2">{option.payload.parentName}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CitySearchInput;